import React, { useEffect, useState } from 'react';
import { Menu } from "antd";
import { useNavigate } from 'react-router-dom';
import api from "../../services/axiosInstance";
import { isModulePermissionKey, isAnyPermissionAllowed, REPORT_PERMISSION_KEYS } from "../../utils/permissionHelper";
import {
    SlidersOutlined,
    SnippetsOutlined,
    KeyOutlined,
    LogoutOutlined,
    SettingOutlined,
    TeamOutlined,
    PlusCircleOutlined,
    UnorderedListOutlined,
    HistoryOutlined,
    DesktopOutlined,
    BarChartOutlined,
    UserOutlined,
    ApartmentOutlined,
    ToolOutlined,
    SafetyCertificateOutlined,
    TagsOutlined,
    CalculatorOutlined,
    EnvironmentOutlined,
    ShoppingOutlined,
    ShoppingCartOutlined,
    DashboardOutlined,
    FileDoneOutlined,
    TruckOutlined,
    GlobalOutlined,
    ExperimentOutlined,
    BuildOutlined,
    AppstoreOutlined,
    ClusterOutlined,
    FireOutlined,
    LockOutlined,
    AccountBookOutlined,
    DollarCircleOutlined,
    WalletOutlined,
    TrophyOutlined,
    CalendarOutlined,
} from '@ant-design/icons';

import "../../styles/fluidDesign.css";

const Navbarmenu = ({ mode = 'horizontal' }) => {
    const navigate = useNavigate();
    const [, setPermissionsVersion] = useState(0);
    const getCurrentUser = () => {
        try {
            return JSON.parse(localStorage.getItem('USER') || '{}');
        } catch (error) {
            return {};
        }
    };

    const isAdminRole = () => {
        const user = getCurrentUser();
        return String(user?.roleId) === '1' || String(user?.role || '').toLowerCase().includes('admin');
    };

    useEffect(() => {
        const refreshNavPermissions = async () => {
            const user = localStorage.getItem('USER');
            if (!user) {
                return;
            }

            try {
                const parsedUser = JSON.parse(user);
                const roleId = parsedUser?.roleId;
                if (!roleId) {
                    return;
                }

                const response = await api.get(`/settings/security/permission/listall.php?role_id=${roleId}`);
                const permissions = response.data?.dataset || [];
                const hasSuppliersPermission = permissions.some((perm) => String(perm.per_name || '').trim() === 'settings_suppliers');

                permissions.forEach((perm) => {
                    const permissionName = String(perm.per_name || '').trim();
                    if (!permissionName) {
                        return;
                    }

                    const isDisabled = Number(perm.per_status) !== 1;
                    localStorage.setItem(`USERPERMISSION_${permissionName}`, isDisabled.toString());
                });

                if (!hasSuppliersPermission && String(roleId) === '1') {
                    localStorage.setItem('USERPERMISSION_settings_suppliers', 'false');
                }

                // Self-heal: ensure harvesting granular permissions exist so
                // the Harvest Batches page buttons render correctly.
                const HARVESTING_SUB_PERMS = [
                    'harvesting_management',
                    'harvesting_management_harvest',
                    'harvesting_harvest_batches_view',
                    'harvesting_harvest_batches_create',
                    'harvesting_harvest_batches_edit',
                    'harvesting_harvest_batches_delete',
                    'harvesting_harvest_batches_view_details',
                    'production',
                    'prod_batches',
                    'prod_batches_view',
                    'prod_batches_create',
                    'prod_batches_edit',
                    'prod_batches_delete',
                    'prod_batches_details',
                    'prod_batches_start',
                    'prod_batches_finish',
                    'prod_batches_export',
                    'prod_process',
                    'prod_process_view',
                    'prod_process_input',
                    'prod_process_approve',
                    'prod_process_qc',
                    'prod_process_export',
                    'prod_logs',
                    'prod_logs_view',
                    'prod_logs_export',
                ];
                const missingHarvestingPerms = HARVESTING_SUB_PERMS.filter(
                    (p) => !permissions.some((perm) => String(perm.per_name || '').trim() === p)
                );
                if (missingHarvestingPerms.length > 0) {
                    const roleData = permissions.length > 0 ? permissions[0] : null;
                    const defaultStatus = String(roleId) === '1' ? 1 : 0;
                    for (const permName of missingHarvestingPerms) {
                        try {
                            await api.post('/settings/security/permission/create.php', {
                                role_id: roleId,
                                per_name: permName,
                                per_status: defaultStatus,
                            });
                            localStorage.setItem(`USERPERMISSION_${permName}`, defaultStatus === 1 ? 'false' : 'true');
                            permissions.push({ per_name: permName, per_status: defaultStatus });
                        } catch (e) {
                            // ignore individual failures
                        }
                    }
                }

                setPermissionsVersion((version) => version + 1);
            } catch (error) {
                console.error('Failed to refresh navigation permissions:', error);
            }
        };

        refreshNavPermissions();
        window.addEventListener('permissionsUpdated', refreshNavPermissions);

        return () => {
            window.removeEventListener('permissionsUpdated', refreshNavPermissions);
        };
    }, []);

    const getPermissionState = (permName) => {
        const value = localStorage.getItem(`USERPERMISSION_${permName}`);
        if (value === null) return 'missing';
        return value === 'true' ? 'disabled' : 'enabled';
    };

    const isDisabled = (permName) => {
        return getPermissionState(permName) !== 'enabled';
    };

    const isAllowed = (permName) => {
        // A leaf may require any one of several permissions (e.g. the Reports
        // menu is shown when the user holds at least one report permission).
        if (Array.isArray(permName)) {
            return isAnyPermissionAllowed(permName);
        }

        if (permName === 'settings_suppliers' && getPermissionState(permName) === 'missing' && isAdminRole()) {
            return true;
        }

        // Material Request / Inventory Issuing: admins see these sections even
        // before their permission rows are seeded.
        if (isAdminRole() && getPermissionState(permName) === 'missing' && isModulePermissionKey(permName)) {
            return true;
        }

        return !isDisabled(permName);
    };

    const logout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const rawMenuItems = [
        {
            key: 'dashboard',
            icon: <DashboardOutlined />,
            label: 'Dashboard',
            onClick: () => navigate('/dash'),
        },

        // ── Sales Products ─────────────────────────────────────
        {
            key: 'sales_products',
            icon: <ShoppingCartOutlined />,
            label: 'Sales Products',
            parentKey: 'sales_products',
            children: [
                {
                    key: 'sales_products_list',
                    icon: <UnorderedListOutlined />,
                    label: 'Product List',
                    permKey: 'sales_products_list',
                    onClick: () => navigate('/sales-products'),
                },
            ],
        },

        // ── Customers ──────────────────────────────────────────
        {
            key: 'customers',
            icon: <TeamOutlined />,
            label: 'Customers',
            parentKey: 'customers',
            children: [
                {
                    key: 'customers_customerList',
                    icon: <UnorderedListOutlined />,
                    label: 'Customer List',
                    permKey: 'customers_list',
                    onClick: () => navigate('/customers'),
                },
            ],
        },

        // ── Tasks ──────────────────────────────────────────────
        // Tasks feature is disabled: routes + imports are commented out in
        // App.js and there is no tasks backend (pages call /todo/* endpoints).
        // Re-enable this menu block when the Tasks module is implemented.
        /*
        {
            key: 'tasks',
            icon: <SnippetsOutlined />,
            label: 'Tasks',
            parentKey: 'tasks',
            children: [
                {
                    key: 'tasks_createTask',
                    icon: <PlusCircleOutlined />,
                    label: 'Create Task',
                    permKey: 'tasks_createTask',
                    onClick: () => navigate('/createtask'),
                },
                {
                    key: 'tasks_taskList',
                    icon: <UnorderedListOutlined />,
                    label: 'Tasks List',
                    permKey: 'tasks_taskList',
                    onClick: () => navigate('/taskList'),
                },
                {
                    key: 'tasks_taskHistory',
                    icon: <HistoryOutlined />,
                    label: 'Tasks History',
                    permKey: 'tasks_taskHistory',
                    onClick: () => navigate('/taskhistorybydate'),
                },
                {
                    key: 'tasks_taskBoard',
                    icon: <CheckSquareOutlined />,
                    label: 'Tasks Board',
                    permKey: 'tasks_taskBoard',
                    onClick: () => navigate('/todoboard'),
                },
            ],
        },
        */

        // ── Farm Setup ─────────────────────────────────────────
        {
            key: 'farmSetUp',
            icon: <GlobalOutlined />,
            label: 'Farm Setup',
            parentKey: 'farmSetUp',
            children: [
                {
                    key: 'farmSetUp_farmOverview',
                    icon: <ClusterOutlined />,
                    label: 'Farm Overview',
                    permKey: 'farmSetUp_farmOverview',
                    onClick: () => navigate('/farmoverview'),
                },
                {
                    key: 'farmSetUp_manageLands',
                    icon: <EnvironmentOutlined />,
                    label: 'Manage Lands',
                    permKey: 'farmSetUp_manageLands',
                    onClick: () => navigate('/land'),
                },
                {
                    key: 'farmSetUp_manageSections',
                    icon: <AppstoreOutlined />,
                    label: 'Manage Sections',
                    permKey: 'farmSetUp_manageSections',
                    onClick: () => navigate('/section'),
                },
                {
                    key: 'farmSetUp_manageFields',
                    icon: <BuildOutlined />,
                    label: 'Manage Fields',
                    permKey: 'farmSetUp_manageFields',
                    onClick: () => navigate('/fields'),
                },
                {
                    key: 'farmSetUp_managePlants',
                    icon: <FireOutlined />,
                    label: 'Manage Plants',
                    permKey: 'farmSetUp_managePlants',
                    onClick: () => navigate('/plants'),
                },
            ],
        },

        // ── Harvesting ──────────────────────────────────────
        {
            key: 'harvesting',
            icon: <TrophyOutlined />,
            label: 'Harvesting',
            parentKey: 'harvesting',
            children: [
                {
                    key: 'harvesting_harvest_batches',
                    icon: <UnorderedListOutlined />,
                    label: 'Harvest Batches',
                    permKey: 'harvesting_harvest_batches_view',
                    onClick: () => navigate('/harvesting/harvest-batches'),
                },
                {
                    key: 'harvesting_harvest_collection',
                    icon: <ExperimentOutlined />,
                    label: 'Harvest Collection',
                    permKey: 'harvesting_management',
                    onClick: () => navigate('/harvesting/harvest-collection'),
                },
            ],
        },

        // ── Production ─────────────────────────────────────────
        {
            key: 'production',
            icon: <ExperimentOutlined />,
            label: 'Production',
            parentKey: 'production',
            children: [
                {
                    key: 'prod_batches_view',
                    icon: <UnorderedListOutlined />,
                    label: 'Production Batches',
                    permKey: 'prod_batches_view',
                    onClick: () => navigate('/production/production-batches'),
                },
                {
                    key: 'prod_process_view',
                    icon: <ExperimentOutlined />,
                    label: 'Production Process',
                    permKey: 'prod_process_view',
                    onClick: () => navigate('/production/production-process'),
                },
                {
                    key: 'prod_logs_view',
                    icon: <HistoryOutlined />,
                    label: 'Production Logs',
                    permKey: 'prod_logs_view',
                    onClick: () => navigate('/production/production-logs'),
                },
            ],
        },

        // ── Inventory ──────────────────────────────────────────
        {
            key: 'inventory',
            icon: <ShoppingOutlined />,
            label: 'Inventory',
            parentKey: 'inventory',
            children: [
                {
                    key: 'inventory_categories',
                    icon: <TagsOutlined />,
                    label: 'Categories',
                    permKey: 'inventory_categories',
                    onClick: () => navigate('/inventory-category'),
                },
                {
                    key: 'inventory_items',
                    icon: <ShoppingOutlined />,
                    label: 'Items',
                    permKey: 'inventory_items',
                    onClick: () => navigate('/inventory-items'),
                },
                {
                    key: 'inventory_stockAdjustments',
                    icon: <HistoryOutlined />,
                    label: 'Stock Adjustments',
                    permKey: 'inventory_stockAdjustments',
                    onClick: () => navigate('/inventory-adjustments'),
                },
                {
                    key: 'inventory-section-purchase-requests',
                    label: <span className="fluid-menu-section-label-text">Purchase Requests</span>,
                    disabled: true,
                    className: 'fluid-menu-section-label',
                    isSectionLabel: true,
                },
                {
                    key: 'inventory_purchase_requests',
                    icon: <ShoppingCartOutlined />,
                    label: 'Purchase Requests',
                    permKey: 'inventory_purchase_requests',
                    onClick: () => navigate('/inventory-purchase-requests'),
                },
                {
                    key: 'inventory_purchase_requests_approvals',
                    icon: <SafetyCertificateOutlined />,
                    label: 'PR Approvals',
                    permKey: 'inventory_purchase_requests_approvals',
                    onClick: () => navigate('/inventory-purchase-requests/approvals'),
                },
                {
                    key: 'inventory-section-grn',
                    label: <span className="fluid-menu-section-label-text">GRN</span>,
                    disabled: true,
                    className: 'fluid-menu-section-label',
                    isSectionLabel: true,
                },
                // ── Goods Received (GRN) ───────────────────────────────────────
                {
                    key: 'grn-create',
                    icon: <PlusCircleOutlined />,
                    label: 'Create GRN',
                    permKey: 'grn_create',
                    onClick: () => navigate('/grn/create'),
                },
                {
                    key: 'grn-list',
                    icon: <UnorderedListOutlined />,
                    label: 'GRN List',
                    permKey: 'grn_list',
                    onClick: () => navigate('/grn/list'),
                },
                {
                    key: 'grn-approvals',
                    icon: <SafetyCertificateOutlined />,
                    label: 'GRN Approvals',
                    permKey: 'grn_approvals',
                    onClick: () => navigate('/grn/approvals'),
                },
                {
                    key: 'inventory-section-material-requests',
                    label: <span className="fluid-menu-section-label-text">Material Requests</span>,
                    disabled: true,
                    className: 'fluid-menu-section-label',
                    isSectionLabel: true,
                },
                // ── Material Requests ─────────────────────────────────────────
                {
                    key: 'material-requests-create',
                    icon: <PlusCircleOutlined />,
                    label: 'Create Material Request',
                    permKey: 'material_request_create',
                    onClick: () => navigate('/inventory/material-request/create'),
                },
                {
                    key: 'material-requests-list',
                    icon: <UnorderedListOutlined />,
                    label: 'Material Requests List',
                    permKey: 'material_request_list',
                    onClick: () => navigate('/inventory/material-request/list'),
                },
                {
                    key: 'material-requests-approvals',
                    icon: <SafetyCertificateOutlined />,
                    label: 'Material Request Approvals',
                    permKey: 'material_request_approvals',
                    onClick: () => navigate('/inventory/material-request/approvals'),
                },
                {
                    key: 'material-requests-accepts',
                    icon: <TruckOutlined />,
                    label: 'Accept Items',
                    permKey: 'material_request_accept',
                    onClick: () => navigate('/inventory/material-request/accepts'),
                },
                {
                    key: 'inventory-section-material-issue-note',
                    label: <span className="fluid-menu-section-label-text">Material Issue Note</span>,
                    disabled: true,
                    className: 'fluid-menu-section-label',
                    isSectionLabel: true,
                },
                // ── Material Issue Note ────────────────────────────────────────
                {
                    key: 'material-issue-note-prepare',
                    icon: <PlusCircleOutlined />,
                    label: 'Prepare Issue Note',
                    permKey: 'material_issue_note_prepare',
                    onClick: () => navigate('/material-issue-note/prepare'),
                },
                {
                    key: 'material-issue-note-list',
                    icon: <UnorderedListOutlined />,
                    label: 'Material Issue Notes List',
                    permKey: 'material_issue_note_list',
                    onClick: () => navigate('/material-issue-note/list'),
                },
                {
                    key: 'material-issue-note-ready',
                    icon: <SafetyCertificateOutlined />,
                    label: 'Ready for Issue',
                    permKey: 'material_issue_note_ready',
                    onClick: () => navigate('/material-issue-note/ready'),
                },
                {
                    key: 'material-issue-note-issue',
                    icon: <TruckOutlined />,
                    label: 'Issue Items',
                    permKey: 'material_issue_note_issue',
                    onClick: () => navigate('/material-issue-note/issue'),
                },
            ],
        },
        // ── Maintenance ────────────────────────────────────────
        {
            key: 'maintenance',
            icon: <SnippetsOutlined />,
            label: 'Maintenance',
            parentKey: 'maintenance',
            children: [
                {
                    key: 'maintenance_maintenanceTasks',
                    icon: <SnippetsOutlined />,
                    label: 'Maintenance Tasks',
                    permKey: 'maintenance_maintenanceTasks',
                    onClick: () => navigate('/maintenance'),
                },
            ],
        },

        // ── Expenses ───────────────────────────────────────────
        {
            key: 'expenses',
            icon: <AccountBookOutlined />,
            label: 'Expenses',
            parentKey: 'expenses',
            children: [
                {
                    key: 'expenses_list',
                    icon: <UnorderedListOutlined />,
                    label: 'Expenses List',
                    permKey: 'expenses_list',
                    onClick: () => navigate('/expenses/list'),
                },
                {
                    key: 'expenses_approve',
                    icon: <SafetyCertificateOutlined />,
                    label: 'Approve Expenses',
                    permKey: 'expenses_approve',
                    onClick: () => navigate('/expenses/approve'),
                },
                {
                    key: 'expenses_payment_create',
                    icon: <FileDoneOutlined />,
                    label: 'Create Payment',
                    permKey: 'expenses_payment_create',
                    onClick: () => navigate('/expenses/payment/create'),
                },
                {
                    key: 'expenses_payment_settle',
                    icon: <DollarCircleOutlined />,
                    label: 'Payment Settle',
                    permKey: 'expenses_payment_settle',
                    onClick: () => navigate('/expenses/payment/settle'),
                },
            ],
        },

        // ── Reports ────────────────────────────────────────────
        // Single entry point: the All Reports page groups every report that
        // the current user is allowed to see and links to the existing
        // report-generation routes. The item is shown when the user holds at
        // least one report permission (see REPORT_PERMISSION_KEYS below).
        {
            key: 'reports',
            icon: <BarChartOutlined />,
            label: 'Reports',
            parentKey: 'reports',
            children: [
                {
                    key: 'reports_all',
                    icon: <UnorderedListOutlined />,
                    label: 'All Reports',
                    permKey: REPORT_PERMISSION_KEYS,
                    onClick: () => navigate('/reports'),
                },
            ],
        },

        // ── Settings ───────────────────────────────────────────
        {
            key: 'settings',
            icon: <SlidersOutlined />,
            label: 'Settings',
            parentKey: 'settings',
            children: [
                {
                    key: 'settings_plantCategories',
                    icon: <TagsOutlined />,
                    label: 'Plant Categories',
                    permKey: 'settings_plantCategories',
                    onClick: () => navigate('/plant-category'),
                },
                {
                    key: 'settings_salesProductCategories',
                    icon: <TagsOutlined />,
                    label: 'Sales Product Categories',
                    permKey: 'settings_salesProductCategories',
                    onClick: () => navigate('/sales-product-categories'),
                },
                {
                    key: 'settings_maintenanceCategories',
                    icon: <ToolOutlined />,
                    label: 'Maintenance Categories',
                    permKey: 'settings_maintenanceCategories',
                    onClick: () => navigate('/maintain-category'),
                },
                {
                    key: 'settings_users',
                    icon: <UserOutlined />,
                    label: 'Users',
                    permKey: 'settings_users',
                    onClick: () => navigate('/usermanagement'),
                },

                {
                    key: 'settings_suppliers',
                    icon: <TruckOutlined />,
                    label: 'Suppliers',
                    permKey: 'settings_suppliers',
                    onClick: () => navigate('/suppliermanagement'),
                },
                {
                    key: 'settings_uoms',
                    icon: <CalculatorOutlined />,
                    label: 'Units of Measurement',
                    permKey: 'settings_uoms',
                    onClick: () => navigate('/uom'),
                },
                {
                    key: 'settings_harvestSeasons',
                    icon: <CalendarOutlined />,
                    label: 'Harvest Seasons',
                    permKey: 'settings_harvestseasons',
                    onClick: () => navigate('/harvest-seasons'),
                },
                {
                    key: 'settings_expenseCategories',
                    icon: <AccountBookOutlined />,
                    label: 'Expense Categories',
                    permKey: 'settings_expenseCategories',
                    onClick: () => navigate('/expense-category'),
                },
                {
                    key: 'settings_paymentAccounts',
                    icon: <WalletOutlined />,
                    label: 'Payment Accounts',
                    permKey: 'settings_paymentAccounts',
                    onClick: () => navigate('/payment-accounts'),
                },
                {
                    key: 'settings_customerAreas',
                    icon: <EnvironmentOutlined />,
                    label: 'Customer Areas',
                    permKey: 'settings_customerAreas',
                    onClick: () => navigate('/customer-areas'),
                },
                {
                    key: 'settings_rolePermissions',
                    icon: <LockOutlined />,
                    label: 'Role Permissions',
                    permKey: 'settings_rolePermissions',
                    onClick: () => navigate('/manageroles'),
                },
            ],
        },
    ];

    // A parent (top-level section or nested submenu) is shown when its own
    // permission is enabled, or when the permission row is missing but it has
    // at least one allowed child (matches the legacy behaviour).
    const isParentAllowed = (parentKey, allowedChildCount) => {
        const parentState = getPermissionState(parentKey);
        return parentState === 'enabled' || (parentState === 'missing' && allowedChildCount > 0);
    };

    // Recursively filters a menu item's children, keeping submenus (items with
    // their own children + parentKey) and leaf items (items with permKey) that
    // the current user is allowed to see. Internal metadata props (parentKey /
    // permKey) are stripped so they don't leak to the DOM (antd warns about
    // unknown props).
    const filterMenuChildren = (children) => {
        const allowed = [];

        for (const child of children || []) {
            if (child.isSectionLabel) {
                const { isSectionLabel, ...sectionLabel } = child;
                allowed.push(sectionLabel);
                continue;
            }

            // Divider items (e.g. visual separators between groups) pass through
            // as-is - they have no permission gate. Leading and consecutive
            // dividers are dropped so a role missing an entire group (e.g. no
            // GRN permissions) doesn't see double separator lines.
            if (child.type === 'divider') {
                const prevIsDivider = allowed.length > 0 && allowed[allowed.length - 1].type === 'divider';
                if (allowed.length > 0 && !prevIsDivider) {
                    allowed.push(child);
                }
                continue;
            }

            // Nested submenu (kept generic for future grouped menus; the
            // Inventory section itself is now flat)
            if (child.children && child.children.length > 0) {
                const allowedSubChildren = filterMenuChildren(child.children);
                if (allowedSubChildren.length === 0) {
                    continue;
                }

                if (!isParentAllowed(child.parentKey, allowedSubChildren.length)) {
                    continue;
                }

                const { parentKey, ...subItem } = child;
                allowed.push({ ...subItem, children: allowedSubChildren });
            } else if (isAllowed(child.permKey)) {
                // Leaf item
                const { permKey, ...leafItem } = child;
                allowed.push(leafItem);
            }
        }

        const hasSectionChildren = (index) => {
            for (let i = index + 1; i < allowed.length; i += 1) {
                if (allowed[i].className === 'fluid-menu-section-label') {
                    return false;
                }
                if (allowed[i].type !== 'divider') {
                    return true;
                }
            }

            return false;
        };

        const cleanedAllowed = allowed.filter((item, index) => {
            if (item.className !== 'fluid-menu-section-label') {
                return true;
            }

            return hasSectionChildren(index);
        });

        // Drop a trailing divider left behind when the last group was filtered out.
        while (cleanedAllowed.length > 0 && cleanedAllowed[cleanedAllowed.length - 1].type === 'divider') {
            cleanedAllowed.pop();
        }

        return cleanedAllowed;
    };

    const getFilteredMenuItems = () => {
        const filtered = [];

        for (const item of rawMenuItems) {
            // Dashboard
            if (!item.parentKey) {
                filtered.push(item);
                continue;
            }

            const allowedChildren = filterMenuChildren(item.children);

            if (allowedChildren.length === 0 || !isParentAllowed(item.parentKey, allowedChildren.length)) {
                continue;
            }

            // Strip internal metadata props so they don't leak to the DOM (antd Menu warns about unknown props)
            const { parentKey, ...parentItem } = item;
            filtered.push({ ...parentItem, children: allowedChildren });
        }

        return filtered;
    };

    const menuItems = getFilteredMenuItems();

    const mobileMenuItems = [
        ...menuItems,
        {
            key: 'account',
            icon: <SettingOutlined />,
            label: 'My Account',
            children: [
                {
                    key: 'change-password',
                    icon: <KeyOutlined />,
                    label: 'Change Password',
                    onClick: () => navigate('/updatepassword'),
                },
                {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Sign Out',
                    onClick: logout,
                    danger: true,
                },
            ],
        },
    ];

    return (
        <Menu
            mode={mode}
            selectable={false}
            items={mode === 'inline' ? mobileMenuItems : menuItems}
            className={mode === 'horizontal' ? 'fluid-nav-menu' : 'fluid-nav-menu-mobile'}
            style={{
                minWidth: 0,
                flex: mode === 'horizontal' ? 1 : 'none',
                background: 'transparent',
                borderBottom: 'none',
            }}
        />
    );
};

export default Navbarmenu;
